import React from 'react'
import PropTypes from 'prop-types'
import { format, isSameDay, parseISO } from 'date-fns'
import { Text, ContentLoader } from 'shared/components'

import { GameCard } from './components/gameCard'
import * as styles from './styles'

const TournamentGamesTabComponent = ({ games, loading, error }) => {
	if (loading) {
		return (
			<div css={styles.container}>
				<ContentLoader loading={true}>
					<div css={styles.loadingPlaceholder}>
						<Text size={12} css={styles.loadingText}>
							Loading games...
						</Text>
					</div>
				</ContentLoader>
			</div>
		)
	}

	if (error) {
		return (
			<div css={styles.container}>
				<div css={styles.errorContainer}>
					<Text size={14} color="red">
						Failed to load games
					</Text>
				</div>
			</div>
		)
	}

	if (!games || games.length === 0) {
		return (
			<div css={styles.container}>
				<div css={styles.emptyContainer}>
					<Text size={14}>
						No games available for this tournament
					</Text>
				</div>
			</div>
		)
	}

	// Group games by date
	const gamesByDate = games.reduce((acc, game) => {
		if (!game.startTime) return acc

		const gameDate = parseISO(game.startTime)
		const dateKey = format(gameDate, 'yyyy-MM-dd')

		if (!acc[dateKey]) {
			acc[dateKey] = {
				date: gameDate,
				games: []
			}
		}

		acc[dateKey].games.push(game)
		return acc
	}, {})

	// Sort dates and games within each date
	const sortedDates = Object.keys(gamesByDate).sort((a, b) =>
		a.localeCompare(b)
	)

	return (
		<div css={styles.container}>
			{sortedDates.map(dateKey => {
				const dateInfo = gamesByDate[dateKey]
				const today = new Date()
				const isToday = isSameDay(dateInfo.date, today)

				// Format date display
				let dateDisplay
				if (isToday) {
					dateDisplay = `Today, ${format(dateInfo.date, 'MMMM d')}`
				} else {
					const isThisYear = dateInfo.date.getFullYear() === today.getFullYear()
					dateDisplay = isThisYear
						? format(dateInfo.date, 'MMMM d')
						: format(dateInfo.date, 'MMMM d, yyyy')
				}

				// Sort games by time within the date
				const sortedGames = dateInfo.games.sort((a, b) => {
					if (!a.startTime || !b.startTime) return 0
					return new Date(a.startTime) - new Date(b.startTime)
				})

				return (
					<div key={dateKey} css={styles.dateSection}>
						<div css={styles.dateHeader}>
							<Text size={16} bold css={styles.dateText}>
								{dateDisplay}
							</Text>
						</div>

						<div css={styles.gamesContainer}>
							{sortedGames.map((game, index) => (
								<GameCard
									key={game.id || `game-${dateKey}-${index}`}
									game={game}
								/>
							))}
						</div>
					</div>
				)
			})}


		</div>
	)
}

TournamentGamesTabComponent.propTypes = {
	games: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		startTime: PropTypes.string,
		teamA: PropTypes.string,
		teamB: PropTypes.string,
		teamOne: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
		teamTwo: PropTypes.shape({
			logo: PropTypes.string,
			teamName: PropTypes.string,
		}),
		resolved: PropTypes.bool,
		winner: PropTypes.string,
	})),
	loading: PropTypes.bool,
	error: PropTypes.string,
}

export default TournamentGamesTabComponent
